import { useEffect, useState, type ReactNode } from 'react';

import {
  ENDPOINTS,
  type ExtractionFieldChange,
  type ExtractionFieldEntry,
  type ExtractionFieldHistoryEntry,
  type ExtractionFieldLimits,
  type ListExtractionFieldsResponse,
} from '@clm/shared-types';

import { changeExtractionField, listExtractionFields } from '../api/extraction-fields';
import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { requireSurface } from '../routes/surfaces';
import {
  ADD_COST_COPY,
  DEACTIVATE_CONFIRM_COPY,
  EXTRACTION_FIELDS_FORBIDDEN_COPY,
  FIELD_SET_ADMIN_ROLES,
  GOVERNED_COPY,
  fieldSetProblemOf,
  type FieldSetProblem,
} from './extraction-fields-shared';
import { FieldSetProblemNotice } from './ExtractionFieldsRefusal';
import { PanelProblem } from './quality/panel';
import { usePanel } from './quality/panel-state';

/**
 * Admin **Extracted fields** — CLM-109 (FR-33, DR-09, NFR-17, CR-07;
 * R-109-1 … R-109-15).
 *
 * An admin defines extracted fields without a code change or a deployment:
 * a key, a description the model reads, and a value bound. The next extraction
 * asks for the field and its value arrives in the verification queue as a row
 * named `additional_terms.<key>`. One read, `GET /v1/admin/extraction-fields`,
 * and one write, `POST /v1/admin/extraction-fields/change` — both
 * `admin:configure`, Admin only, no Legal co-sign (D-2.1-7).
 *
 * ## The budget, stated
 *
 * Each admin field is an entry in a fixed list the extraction request already
 * carries, so it costs **none** of the union-typed parameters Anthropic limits
 * (the request uses 14 of 16, and the repo's ceiling is 14). The panel says so
 * with the API's own figures — the same check a save runs — alongside the
 * admin-field cap. Every figure is the API's; the page formats, never computes
 * (FR-27).
 *
 * ## Every change names the revision the page saw
 *
 * Each save carries `expected_revision` — the revision this page showed — so a
 * change another admin made in the meantime is `409 field_set_changed`, and
 * the page re-reads rather than overwriting a decision nobody here has seen. A
 * refusal the API names is explained in the page's own words, with the cap and
 * the correlation id (`FieldSetProblemNotice`, the CLM-111 precedent).
 *
 * ## Nothing is ever deleted
 *
 * Deactivating stops extraction from the next contract and touches nothing
 * else: every value already extracted, and every queue row, stays (CR-07). A
 * retired field comes back by **reactivate**; a key is added once, ever, so its
 * accuracy history never mixes two meanings (R-109-2). The history lists every
 * change, newest first.
 */

const ENDPOINT_PATH = ENDPOINTS['list-extraction-fields'].path;
const PANEL = 'Extracted fields';

function when(instant: string): ReactNode {
  return <time dateTime={instant}>{instant}</time>;
}

const HISTORY_COLUMNS: readonly DataTableColumn<ExtractionFieldHistoryEntry>[] = [
  { id: 'revision', header: 'Revision', cell: (row) => `r${String(row.field_set_revision)}`, cellClassName: 'num' },
  { id: 'field', header: 'Field', cell: (row) => <code>{row.field_key}</code> },
  { id: 'change', header: 'Change', cell: (row) => row.change_kind },
  {
    id: 'who',
    header: 'By',
    cell: (row) => (
      <>
        {row.changed_by_upn} · {when(row.changed_utc)}
      </>
    ),
  },
  { id: 'note', header: 'Note', cell: (row) => row.change_note ?? <span className="mut">—</span> },
];

export function ExtractionFields() {
  const surface = requireSurface('extraction-fields');
  const { identity } = useIdentity();
  const mayChange = mayAttempt(identity, FIELD_SET_ADMIN_ROLES);
  const { state, reload } = usePanel<ListExtractionFieldsResponse>((signal) => listExtractionFields({ signal }), []);
  /* The last list stays on screen while the reload after a change is in flight, so the outcome it reports is not unmounted by it. */
  const [lastReady, setLastReady] = useState<ListExtractionFieldsResponse | null>(null);
  useEffect(() => {
    if (state.kind === 'ready') setLastReady(state.response);
  }, [state]);
  const shown = state.kind === 'ready' ? state.response : state.kind === 'loading' ? lastReady : null;

  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [problem, setProblem] = useState<FieldSetProblem | null>(null);

  /** One change at the revision this page showed. `true` when it was saved. */
  async function submit(response: ListExtractionFieldsResponse, change: ExtractionFieldChange, success: string): Promise<boolean> {
    setSaving(true);
    setDone(null);
    setProblem(null);
    try {
      const saved = await changeExtractionField({ expected_revision: response.current_revision, change });
      setDone(`${success} Saved as revision ${String(saved.field_set_revision)}; the next extraction uses it.`);
      reload();
      return true;
    } catch (error) {
      const failure = fieldSetProblemOf(error, response.limits);
      setProblem(failure);
      /* Another admin moved the set: show what it is now. */
      if (failure.refusal?.code === 'field_set_changed') reload();
      return false;
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="hd">
        <div>
          <h2 id="extraction-fields-heading">{surface.label}</h2>
          <p>{surface.summary}</p>
          <div className="refs">
            {surface.requirements.map((requirement) => (
              <span key={requirement}>{requirement}</span>
            ))}
          </div>
        </div>
      </div>

      <section className="card qpanel" aria-labelledby="extraction-fields-heading" aria-busy={state.kind === 'loading'}>
        {state.kind === 'loading' && shown === null ? (
          <p role="status" aria-busy="true">
            Reading {PANEL}…
          </p>
        ) : null}

        {state.kind === 'problem' ? (
          <PanelProblem
            panel={PANEL}
            state={state}
            endpointPath={ENDPOINT_PATH}
            onRetry={reload}
            forbiddenCopy={EXTRACTION_FIELDS_FORBIDDEN_COPY}
          />
        ) : null}

        {shown === null ? null : (
          <>
            <Budget response={shown} />
            <Governed names={shown.governed_fields} />

            <h3>Admin fields</h3>
            <FieldsTable
              response={shown}
              mayChange={mayChange}
              saving={saving}
              onSubmit={(change, success) => submit(shown, change, success)}
            />
            {mayChange ? (
              <AddFieldForm limits={shown.limits} saving={saving} onSubmit={(change, success) => submit(shown, change, success)} />
            ) : null}

            {done === null ? null : (
              <p role="status" data-testid="field-set-done">
                <strong>{done}</strong>
              </p>
            )}
            {problem === null ? null : <FieldSetProblemNotice problem={problem} />}

            <h3>History</h3>
            <DataTable
              caption="Every change to the admin field set, newest first"
              columns={HISTORY_COLUMNS}
              rows={shown.history}
              rowKey={(row) => String(row.field_set_revision)}
              empty={<p className="mut">No admin field has been defined yet — every extraction asks for the governed fields alone.</p>}
            />
          </>
        )}
      </section>
    </>
  );
}

function Budget({ response }: { readonly response: ListExtractionFieldsResponse }) {
  const { limits } = response;
  const active = response.fields.filter((field) => field.is_active).length;
  return (
    <div className="frows" data-testid="field-set-budget">
      <div className="frow-f">
        <span className="fname">Admin fields</span>
        <span className="fval">
          {active} of {limits.active_max} active
        </span>
      </div>
      <div className="frow-f">
        <span className="fname">Union-typed parameters</span>
        <span className="fval">
          {limits.union_count} of {limits.union_limit} (repo ceiling {limits.union_ceiling}); admin fields use none
        </span>
      </div>
      <div className="frow-f">
        <span className="fname">Output schema</span>
        <span className="fval">
          {limits.schema_bytes} of {limits.schema_bytes_max} bytes
        </span>
      </div>
      <div className="frow-f">
        <span className="fname">Revision</span>
        <span className="fval">r{response.current_revision}</span>
      </div>
    </div>
  );
}

function Governed({ names }: { readonly names: readonly string[] }) {
  return (
    <details data-testid="governed-fields">
      <summary>Governed fields ({names.length}) — read-only</summary>
      <p className="mut">{GOVERNED_COPY}</p>
      <ul>
        {names.map((name) => (
          <li key={name}>
            <code>{name}</code>
          </li>
        ))}
      </ul>
    </details>
  );
}

type Submit = (change: ExtractionFieldChange, success: string) => Promise<boolean>;

function FieldsTable({
  response,
  mayChange,
  saving,
  onSubmit,
}: {
  readonly response: ListExtractionFieldsResponse;
  readonly mayChange: boolean;
  readonly saving: boolean;
  readonly onSubmit: Submit;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const columns: readonly DataTableColumn<ExtractionFieldEntry>[] = [
    {
      id: 'field',
      header: 'Field',
      cell: (row) => (
        <>
          <code>{row.field_key}</code>{' '}
          {row.is_active ? <span className="tag up">active</span> : <span className="tag out">retired</span>}
        </>
      ),
    },
    { id: 'description', header: 'What the model is asked', cell: (row) => row.description },
    { id: 'bound', header: 'Value bound', cell: (row) => `${String(row.value_max)} characters`, cellClassName: 'num' },
    {
      id: 'changed',
      header: 'Last change',
      cell: (row) => (
        <>
          {row.last_change_kind} by {row.last_changed_by_upn} · {when(row.last_changed_utc)}
        </>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (row) =>
        !mayChange ? (
          <span className="mut">—</span>
        ) : row.is_active ? (
          <span className="vacts">
            <button type="button" className="btn" disabled={saving} onClick={() => setEditing(row.field_key)}>
              Edit
            </button>
            <button type="button" className="btn" disabled={saving} onClick={() => setConfirming(row.field_key)}>
              Deactivate
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="btn"
            disabled={saving}
            onClick={() => void onSubmit({ kind: 'reactivate', field_key: row.field_key }, `${row.field_key} is active again.`)}
          >
            Reactivate
          </button>
        ),
    },
  ];

  const editRow = response.fields.find((row) => row.field_key === editing) ?? null;
  const confirmRow = response.fields.find((row) => row.field_key === confirming) ?? null;

  return (
    <>
      <DataTable
        caption="Admin-defined fields, active and retired"
        columns={columns}
        rows={response.fields}
        rowKey={(row) => row.field_key}
        empty={<p className="mut">No admin field yet.</p>}
      />
      {confirmRow === null ? null : (
        <div className="banner up" role="group" aria-label={`Deactivate ${confirmRow.field_key}`} data-testid="deactivate-confirm">
          <p>
            Deactivate <code>{confirmRow.field_key}</code>? {DEACTIVATE_CONFIRM_COPY}
          </p>
          <div className="vacts">
            <button
              type="button"
              className="btn pri"
              disabled={saving}
              onClick={() =>
                void onSubmit({ kind: 'deactivate', field_key: confirmRow.field_key }, `${confirmRow.field_key} is retired.`).then(() =>
                  setConfirming(null),
                )
              }
            >
              Deactivate
            </button>
            <button type="button" className="btn qt" disabled={saving} onClick={() => setConfirming(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {editRow === null ? null : (
        <EditFieldForm
          key={editRow.field_key}
          field={editRow}
          limits={response.limits}
          saving={saving}
          onCancel={() => setEditing(null)}
          onSubmit={async (change, success) => {
            const saved = await onSubmit(change, success);
            if (saved) setEditing(null);
            return saved;
          }}
        />
      )}
    </>
  );
}

function DescriptionInput({
  id,
  value,
  limits,
  disabled,
  onChange,
}: {
  readonly id: string;
  readonly value: string;
  readonly limits: ExtractionFieldLimits;
  readonly disabled: boolean;
  readonly onChange: (value: string) => void;
}) {
  /* Code points, as ck_extraction_field_description's length() counts them — `.length` counts an emoji twice. */
  const length = [...value.trim()].length;
  const outside = length > 0 && (length < limits.description_min || length > limits.description_max);
  return (
    <div className="field">
      <label htmlFor={id}>What the model should look for</label>
      <textarea id={id} value={value} disabled={disabled} maxLength={limits.description_max} onChange={(event) => onChange(event.target.value)} />
      <span className={outside ? 'tag bad' : 'mut'} aria-live="polite" data-testid={`${id}-count`}>
        {length} / {limits.description_max} characters (at least {limits.description_min})
      </span>
    </div>
  );
}

function AddFieldForm({
  limits,
  saving,
  onSubmit,
}: {
  readonly limits: ExtractionFieldLimits;
  readonly saving: boolean;
  readonly onSubmit: Submit;
}) {
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [valueMax, setValueMax] = useState('200');
  const [note, setNote] = useState('');

  async function add() {
    const trimmedNote = note.trim();
    const saved = await onSubmit(
      {
        kind: 'add',
        field_key: key.trim(),
        description: description.trim(),
        value_max: Number.parseInt(valueMax, 10) || 0,
        ...(trimmedNote === '' ? {} : { note: trimmedNote }),
      },
      `${key.trim()} was added.`,
    );
    if (saved) {
      setKey('');
      setDescription('');
      setValueMax('200');
      setNote('');
    }
  }

  const ready = key.trim() !== '' && description.trim() !== '' && valueMax.trim() !== '';
  return (
    <div className="vform" role="group" aria-label="Add an admin field" data-testid="field-add">
      <h4>Add a field</h4>
      <p className="mut">{ADD_COST_COPY}</p>
      <div className="field">
        <label htmlFor="field-add-key">Key</label>
        <input id="field-add-key" type="text" value={key} disabled={saving} placeholder="e.g. service_credit_cap" maxLength={48} onChange={(event) => setKey(event.target.value)} />
        <span className="mut">One lower-case snake_case word; stored as additional_terms.{key.trim() === '' ? '<key>' : key.trim()}</span>
      </div>
      <DescriptionInput id="field-add-description" value={description} limits={limits} disabled={saving} onChange={setDescription} />
      <div className="field">
        <label htmlFor="field-add-bound">Value bound (characters, at most {limits.value_max})</label>
        <input id="field-add-bound" type="number" min={1} max={limits.value_max} value={valueMax} disabled={saving} onChange={(event) => setValueMax(event.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="field-add-note">Note (optional)</label>
        <input id="field-add-note" type="text" value={note} disabled={saving} maxLength={500} onChange={(event) => setNote(event.target.value)} />
      </div>
      <div className="vacts">
        <button type="button" className="btn pri" disabled={saving || !ready} onClick={() => void add()}>
          {saving ? 'Saving…' : 'Add field'}
        </button>
      </div>
    </div>
  );
}

function EditFieldForm({
  field,
  limits,
  saving,
  onCancel,
  onSubmit,
}: {
  readonly field: ExtractionFieldEntry;
  readonly limits: ExtractionFieldLimits;
  readonly saving: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: Submit;
}) {
  const [description, setDescription] = useState(field.description);
  const [valueMax, setValueMax] = useState(String(field.value_max));

  function save() {
    const bound = Number.parseInt(valueMax, 10) || 0;
    const nextDescription = description.trim();
    const changedDescription = nextDescription !== field.description;
    /* An edit that changes nothing still names the bound, so the request parses
     * and the API answers `no_change` — in the page's own words, not a guess here. */
    const sendBound = bound !== field.value_max || !changedDescription;
    void onSubmit(
      {
        kind: 'edit',
        field_key: field.field_key,
        ...(changedDescription ? { description: nextDescription } : {}),
        ...(sendBound ? { value_max: bound } : {}),
      },
      `${field.field_key} was edited.`,
    );
  }

  return (
    <div className="vform" role="group" aria-label={`Edit ${field.field_key}`} data-testid="field-edit">
      <h4>
        Edit <code>{field.field_key}</code>
      </h4>
      <p className="mut">A changed description or bound applies from the next contract; values already extracted keep the definition they were asked with.</p>
      <DescriptionInput id="field-edit-description" value={description} limits={limits} disabled={saving} onChange={setDescription} />
      <div className="field">
        <label htmlFor="field-edit-bound">Value bound (characters, at most {limits.value_max})</label>
        <input id="field-edit-bound" type="number" min={1} max={limits.value_max} value={valueMax} disabled={saving} onChange={(event) => setValueMax(event.target.value)} />
      </div>
      <div className="vacts">
        <button type="button" className="btn pri" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Save change'}
        </button>
        <button type="button" className="btn qt" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
